import { Scale, BookOpenCheck, Users, HeartHandshake } from "lucide-react";
import { Reveal, SectionHeader } from "./SectionHeader";
import { goTo } from "../lib/scroll";

const BLOCKS = [
  {
    icon: Scale,
    title: "Un derecho, no un favor",
    text: "Entender la información permite decidir con libertad. Por eso comunicarnos de forma clara es un compromiso social.",
  },
  {
    icon: BookOpenCheck,
    title: "Un método con norma",
    text: "La lectura fácil sigue la norma UNE 153101. Lenguaje sencillo, una idea por frase y un formato claro. No es resumir: es escribir pensando en quien lee.",
  },
  {
    icon: Users,
    title: "Siempre se valida",
    text: "Un texto es lectura fácil cuando personas con dificultades de comprensión lo prueban y confirman que se entiende.",
  },
  {
    icon: HeartHandshake,
    title: "Para todo el mundo",
    text: "Personas con discapacidad intelectual, mayores, personas migrantes que aprenden el idioma… y cualquiera frente a un texto burocrático.",
  },
];

export default function WhatIs() {
  return (
    <section id="que-es" className="py-20 md:py-28">
      <div className="max-w-6xl mx-auto px-5 md:px-8">
        <SectionHeader
          eyebrow="¿Qué es la lectura fácil?"
          title="Escribir fácil es difícil. Y por eso funciona."
          text="La lectura fácil adapta textos a un lenguaje y un formato claros, comprensibles y accesibles. Sin infantilizar. Sin resumir sin más."
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-14" data-testid="what-is-blocks">
          {BLOCKS.map((b, i) => (
            <Reveal key={b.title} delay={i * 0.08} className="h-full">
              <article
                className="h-full sketch bg-[var(--card)] p-6 flex flex-col gap-4 hover:-translate-y-1 transition-transform shadow-[5px_5px_0_var(--ink)]"
                data-testid={`what-is-block-${i + 1}`}
              >
                <span className="inline-flex items-center justify-center w-12 h-12 sketch-sm bg-[var(--blue)] text-white">
                  <b.icon className="w-6 h-6" aria-hidden="true" />
                </span>
                <h3 className="font-display font-bold text-xl leading-snug">{b.title}</h3>
                <p className="text-base leading-relaxed text-[var(--muted)]">{b.text}</p>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1} className="mt-16">
          <figure className="grid md:grid-cols-[0.9fr_1.1fr] gap-8 items-center sketch-3 bg-[var(--card)] p-6 md:p-10" data-testid="what-is-quote">
            <div className="sketch overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1460518451285-97b6aa326961?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Dos personas leyendo libros y tomando notas juntas en una mesa"
                className="w-full h-64 md:h-72 object-cover"
                loading="lazy"
              />
            </div>
            <blockquote>
              <p className="font-display font-bold text-2xl md:text-3xl leading-snug">
                «La accesibilidad cognitiva es una deuda social. Hacer las cosas accesibles es tan sencillo como hacerlas{" "}
                <span className="marker">muy fácil</span>.»
              </p>
              <figcaption className="mt-5 text-base font-bold text-[var(--muted)]">
                Idea central del proyecto ·{" "}
                <a href="#podcast" data-testid="what-is-quote-link" className="underline decoration-[var(--blue)] decoration-2 underline-offset-4 hover:text-[var(--blue-deep)]" onClick={(e) => { e.preventDefault(); goTo("#podcast"); }}>
                  Escúchalo en el vídeo podcast
                </a>
              </figcaption>
            </blockquote>
          </figure>
        </Reveal>
      </div>
    </section>
  );
}
